import { useState, useEffect, useRef } from 'react';
import { Activity, Question, QuestionType, MultipleChoiceQuestion, TrueFalseQuestion, ShortAnswerQuestion, ActivityEditorProps } from '../types';
import apiClient from '@/lib/api-client';
import toast from 'react-hot-toast';

export const useQuizEditor = ({ activity, onActivityUpdate, onActivityChange }: ActivityEditorProps) => {
  // State
  const [questions, setQuestionsBase] = useState<Question[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [quizTitle, setQuizTitle] = useState(activity.title);
  const [quizDescription, setQuizDescription] = useState(activity.description || '');
  const [isDragging, setIsDragging] = useState(false);
  const [draggedQuestionId, setDraggedQuestionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showQuestionTypeModal, setShowQuestionTypeModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const [showAiDialog, setShowAiDialog] = useState(false);
  
  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Load questions from activity when component mounts or activity changes
  useEffect(() => {
    if (activity.questions && Array.isArray(activity.questions)) {
      const loadedQuestions = activity.questions.map((q: any) => {
        const baseQuestion = {
          id: q.question_id || q.id || `q${Date.now()}`,
          text: q.text || '',
          points: q.points || 1,
          type: q.type as QuestionType || 'multiple_choice',
        };

        if (q.type === 'multiple_choice') {
          return {
            ...baseQuestion,
            type: 'multiple_choice',
            options: Array.isArray(q.options) ? q.options.map((opt: any) => ({
              id: opt.id || `opt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              text: opt.text || '',
              isCorrect: !!opt.isCorrect
            })) : [
              { id: '1', text: '', isCorrect: false },
              { id: '2', text: '', isCorrect: false }
            ]
          } as MultipleChoiceQuestion;
        } else if (q.type === 'true_false') {
          return {
            ...baseQuestion,
            type: 'true_false',
            correctAnswer: q.correctAnswer === true
          } as TrueFalseQuestion;
        } else if (q.type === 'short_answer') {
          return {
            ...baseQuestion,
            type: 'short_answer',
            correctAnswer: q.correctAnswer || ''
          } as ShortAnswerQuestion;
        } else {
          return {
            ...baseQuestion,
            type: 'multiple_choice',
            options: [
              { id: '1', text: '', isCorrect: false },
              { id: '2', text: '', isCorrect: false }
            ]
          } as MultipleChoiceQuestion;
        }
      });

      setQuestionsBase(loadedQuestions);
    }
  }, [activity]);
  
  // Handle AI generated content from parent component
  useEffect(() => {
    const handleAiContent = (event: CustomEvent) => {
      if (event.detail && event.detail.activityId === activity.activity_id) {
        handleAiGeneratedContent(event.detail.content);
      }
    };
    
    window.addEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    
    return () => {
      window.removeEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    };
  }, [activity.activity_id, questions]);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowQuestionTypeModal(false);
      }
    }
    
    if (showQuestionTypeModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQuestionTypeModal]);
  
  // Handle window resize to update dropdown position
  useEffect(() => {
    function handleResize() {
      if (showQuestionTypeModal) {
        determineDropdownPosition();
      }
    }
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showQuestionTypeModal]);

  // Create a new question
  const createNewQuestion = (type: QuestionType): Question => {
    const baseQuestion = {
      id: Date.now().toString(),
      text: '',
      points: 1,
      type,
    };

    switch (type) {
      case 'multiple_choice':
        return {
          ...baseQuestion,
          type: 'multiple_choice',
          options: [
            { id: '1', text: '', isCorrect: false },
            { id: '2', text: '', isCorrect: false },
          ],
        };
      case 'true_false':
        return {
          ...baseQuestion,
          type: 'true_false',
          correctAnswer: true,
        };
      case 'short_answer':
        return {
          ...baseQuestion,
          type: 'short_answer',
          correctAnswer: '',
        };
      case 'boolean':
        return {
          ...baseQuestion,
          type: 'boolean',
          correctAnswer: true,
        };
    }
  };

  // Determine dropdown position based on button position
  const determineDropdownPosition = () => {
    if (!buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    const neededSpace = 300;
    
    if (spaceBelow < neededSpace && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };

  // Show question type selection modal
  const handleShowQuestionTypeModal = () => {
    determineDropdownPosition();
    setShowQuestionTypeModal(true);
  };
  
  // Add a new question with the selected type
  const handleAddQuestion = (type: QuestionType) => {
    const newQuestion = createNewQuestion(type);
    updateQuestions([...questions, newQuestion]);
    setEditingQuestionId(newQuestion.id);
    setShowQuestionTypeModal(false);
  };

  // Get the question being edited
  const getEditingQuestion = () => {
    return questions.find(q => q.id === editingQuestionId) || null;
  };

  // Update a question
  const updateQuestion = (questionId: string, updates: Partial<Question>) => {
    updateQuestions(
      questions.map(q => {
        if (q.id !== questionId) return q;
        
        if (q.type === 'multiple_choice' && 'options' in updates) {
          return { ...q, ...updates } as MultipleChoiceQuestion;
        } else if (q.type === 'true_false' && 'correctAnswer' in updates) {
          return { ...q, ...updates } as TrueFalseQuestion;
        } else if (q.type === 'short_answer' && 'correctAnswer' in updates) {
          return { ...q, ...updates } as ShortAnswerQuestion;
        }
        
        const updatedQuestion = { ...q, ...updates };
        
        if (q.type === 'multiple_choice') {
          return updatedQuestion as MultipleChoiceQuestion;
        } else if (q.type === 'true_false') {
          return updatedQuestion as TrueFalseQuestion;
        } else {
          return updatedQuestion as ShortAnswerQuestion;
        }
      }) as Question[]
    );
  };

  // Delete a question
  const handleDeleteQuestion = (questionId: string) => {
    updateQuestions(questions.filter(q => q.id !== questionId));
    if (editingQuestionId === questionId) {
      setEditingQuestionId(null);
    }
  };

  // Duplicate a question
  const handleDuplicateQuestion = (questionId: string) => {
    const questionToDuplicate = questions.find(q => q.id === questionId);
    if (questionToDuplicate) {
      const duplicatedQuestion = JSON.parse(JSON.stringify(questionToDuplicate)) as Question;
      duplicatedQuestion.id = Date.now().toString();
      duplicatedQuestion.text = `${questionToDuplicate.text} (Copy)`;
      updateQuestions([...questions, duplicatedQuestion]);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, questionId: string) => {
    setIsDragging(true);
    setDraggedQuestionId(questionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, questionId: string) => {
    e.preventDefault();
    if (draggedQuestionId !== questionId) {
      const draggedIndex = questions.findIndex(q => q.id === draggedQuestionId);
      const hoverIndex = questions.findIndex(q => q.id === questionId);
      
      if (draggedIndex !== -1 && hoverIndex !== -1) {
        const newQuestions = [...questions];
        const [removed] = newQuestions.splice(draggedIndex, 1);
        newQuestions.splice(hoverIndex, 0, removed);
        
        updateQuestions(newQuestions as Question[]);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedQuestionId(null);
  };

  // Change question type
  const changeQuestionType = (questionId: string, newType: QuestionType) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.type !== newType) {
      const newQuestion = createNewQuestion(newType);
      
      newQuestion.text = question.text;
      newQuestion.points = question.points;
      
      updateQuestions(questions.map(q => q.id === questionId ? newQuestion : q));
    }
  };

  const updateQuestions = (questions: Question[]) => {
    setQuestionsBase(questions);
    const updatedActivity = {
      ...activity,
      title: quizTitle,
      description: quizDescription,
      questions: questions.map(q => ({
        ...q,
        question_id: q.id.startsWith('q') ? q.id : `q${q.id}`
      }))
    };
    onActivityChange?.(updatedActivity);
  };

  // Save the quiz
  const handleSaveQuiz = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      
      const updatedActivity = {
        ...activity,
        title: quizTitle,
        description: quizDescription,
        questions: questions.map(q => ({
          ...q,
          question_id: q.id.startsWith('q') ? q.id : `q${q.id}`
        }))
      };
      
      if (onActivityUpdate) {
        await onActivityUpdate(updatedActivity);
        console.log('Quiz saved successfully to module structure');
      } else {
        const response = await apiClient.put(`/module/activity/${activity.activity_id}`, updatedActivity);
        
        if (response.status !== 200) {
          throw new Error('Failed to save quiz');
        }
        
        console.log('Quiz saved successfully via direct API call:', response.data);
      }
      
      toast.success('Quiz saved successfully!');
    } catch (error) {
      console.error('Error saving quiz:', error);
      setSaveError('Failed to save quiz. Please try again.');
      toast.error('Error saving quiz. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle AI generated content
  const handleAiGeneratedContent = (generatedContent: any) => {
    console.log('AI generated content:', generatedContent);
    
    if (generatedContent.questions && Array.isArray(generatedContent.questions)) {
      const newQuestions = generatedContent.questions.map((q: any) => {
        const baseQuestion = {
          id: `q${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          text: q.text || '',
          points: q.points || 1,
          type: q.type as QuestionType || 'multiple_choice',
        };

        if (q.type === 'multiple_choice') {
          return {
            ...baseQuestion,
            type: 'multiple_choice',
            options: Array.isArray(q.options) ? q.options.map((opt: any) => ({
              id: `opt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              text: opt.text || '',
              isCorrect: !!opt.isCorrect
            })) : [
              { id: '1', text: '', isCorrect: false },
              { id: '2', text: '', isCorrect: false }
            ]
          } as MultipleChoiceQuestion;
        } else if (q.type === 'true_false') {
          return {
            ...baseQuestion,
            type: 'true_false',
            correctAnswer: q.correctAnswer === true
          } as TrueFalseQuestion;
        } else if (q.type === 'short_answer') {
          return {
            ...baseQuestion,
            type: 'short_answer',
            correctAnswer: q.correctAnswer || ''
          } as ShortAnswerQuestion;
        } else {
          return {
            ...baseQuestion,
            type: 'multiple_choice',
            options: [
              { id: '1', text: '', isCorrect: false },
              { id: '2', text: '', isCorrect: false }
            ]
          } as MultipleChoiceQuestion;
        }
      });

      if (generatedContent.title) {
        setQuizTitle(generatedContent.title);
      }
      
      if (generatedContent.description) {
        setQuizDescription(generatedContent.description);
      }
      
      updateQuestions([...questions, ...newQuestions]);
    } else if (generatedContent.raw_response) {
      toast.error('AI generated content could not be automatically applied. Please try again with a more specific prompt.');
    }
  };

  return {
    // State
    questions,
    editingQuestionId,
    quizTitle,
    quizDescription,
    isDragging,
    isSaving,
    saveError,
    showQuestionTypeModal,
    dropdownPosition,
    showAiDialog,
    
    // Refs
    dropdownRef,
    buttonRef,
    
    // Actions
    setEditingQuestionId,
    setQuizTitle,
    setQuizDescription,
    setShowAiDialog,
    handleShowQuestionTypeModal,
    handleAddQuestion,
    updateQuestion,
    handleDeleteQuestion,
    handleDuplicateQuestion,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    changeQuestionType,
    handleSaveQuiz,
    handleAiGeneratedContent,
    setShowQuestionTypeModal,
  };
}; 